import React, { useState } from "react";
import { C } from "../constants/design";
import { useI18n } from "../i18n/I18nContext";

// Shown when an OAuth login lands on a user with no game account, but the device
// has traces of an existing player that was never linked to this Google user
// (guest fork / legacy placeholder — identity incidents 07/2026).
// One click reconnects the player to the Google account instead of sending the
// user to NicknameSetup, which would create a duplicate empty account.
export function RecoverAccount({
  name,
  hasBackup,
  onRecover,
  onDecline,
  onRestoreBackup,
}: {
  name: string;
  hasBackup: boolean;
  onRecover: () => Promise<string | void>;
  onDecline: () => void;
  onRestoreBackup: () => void;
}) {
  const { t } = useI18n();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const handleRecover = async () => {
    setBusy(true);
    const err = await onRecover();
    if (err) { setError(err); setBusy(false); }
  };

  return (
    <div
      style={{
        minHeight: "100dvh",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        padding: "32px 22px",
        gap: 4,
      }}
    >
      <div style={{ textAlign: "center", marginBottom: 28 }}>
        <div style={{ fontSize: 42, marginBottom: 4 }}>⚽🏆</div>
        <div className="d" style={{ fontSize: 30, fontWeight: 900, letterSpacing: -0.5 }}>
          Prono<span style={{ color: C.turf }}>Cup</span>
        </div>
        <div style={{ fontSize: 16, fontWeight: 800, marginTop: 18 }}>
          {t("recover.title")}
        </div>
        <div style={{ fontSize: 14, color: C.muted, marginTop: 10, lineHeight: 1.5 }}>
          {t("recover.foundAccount", name)}
        </div>
      </div>

      {error && (
        <div style={{ color: "#ef4444", fontSize: 12, fontWeight: 600, marginBottom: 8, textAlign: "center" }}>
          {error}
        </div>
      )}

      <button
        onClick={handleRecover}
        disabled={busy}
        style={{
          border: "none",
          borderRadius: 12,
          padding: "15px",
          fontWeight: 600,
          fontSize: 16,
          background: "#48FF85",
          color: "#06121e",
          cursor: busy ? "default" : "pointer",
          opacity: busy ? 0.8 : 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 8,
        }}
      >
        {busy && (
          <span style={{
            width: 16,
            height: 16,
            border: "2.5px solid rgba(6,18,30,0.25)",
            borderTopColor: "#06121e",
            borderRadius: "50%",
            display: "inline-block",
            animation: "spin 0.7s linear infinite",
          }} />
        )}
        {busy ? t("recover.connecting") : t("recover.connectButton", name)}
      </button>

      <button
        onClick={onDecline}
        disabled={busy}
        style={{
          marginTop: 10,
          border: "none",
          borderRadius: 12,
          padding: "15px",
          fontWeight: 600,
          fontSize: 15,
          background: "rgba(255,255,255,0.18)",
          color: "#fff",
          cursor: busy ? "default" : "pointer",
          width: "100%",
        }}
      >
        {t("recover.notMineButton")}
      </button>

      {hasBackup && (
        <button
          onClick={onRestoreBackup}
          disabled={busy}
          style={{
            marginTop: 12,
            background: "transparent",
            border: "none",
            color: C.faint,
            fontSize: 13,
            cursor: busy ? "default" : "pointer",
            padding: "4px 0",
          }}
        >
          {t("recover.restoreSession")}
        </button>
      )}
    </div>
  );
}
